import {AriaRole, CSSProperties, forwardRef, ReactNode} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {BG_COLOR_CLASSES} from '../../class-maps/bgColorClasses';
import {
  OVERFLOW_CLASSES,
  OVERFLOW_X_CLASSES,
  OVERFLOW_Y_CLASSES,
} from '../../class-maps/overflowClasses';
import {POSITION_CLASSES} from '../../class-maps/positionClasses';
import {RADIUS_CLASSES} from '../../class-maps/radiusClasses';
import {SHADOW_CLASSES} from '../../class-maps/shadowClasses';
import {
  BORDER_STYLE_MAPPINGS,
  FLEX_ITEM_STYLE_MAPPINGS,
  MISC_STYLE_MAPPINGS,
  POSITIONING_STYLE_MAPPINGS,
  SIZING_STYLE_MAPPINGS,
} from '../../class-maps/styleMappings';
import {CSSDimension} from '../../types/CSSDimension';
import {Integer} from '../../types/Integer';
import {isResponsiveValue, ResponsiveValue} from '../../types/ResponsiveValue';
import {
  ThemeColorPath,
  ThemeRadiusPath,
  ThemeShadowsPath,
  ThemeZIndexPath,
} from '../../types/ThemePaths';
import {buildStylesFromSchema, StyleMapping} from '../../utils/buildStylesFromSchema';
import {cn} from '../../utils/cn';
import {getResponsiveStaticClass, getStaticClass} from '../../utils/getSpacingStyle';
import {resolveSpacingValue} from '../../utils/resolveSpacingValue';

// =============================================================================
// PADDING STYLE MAPPINGS (Box-specific)
// =============================================================================

const resolveSpacingTransform = (value: unknown): string | undefined =>
  resolveSpacingValue(value as Integer | ResponsiveValue<Integer>);

const PADDING_STYLE_MAPPINGS: ReadonlyArray<StyleMapping> = [
  {prop: 'padding', styleProp: 'padding', transform: resolveSpacingTransform},
  {prop: 'paddingTop', styleProp: 'paddingTop', transform: resolveSpacingTransform},
  {prop: 'paddingBottom', styleProp: 'paddingBottom', transform: resolveSpacingTransform},
  {prop: 'paddingLeft', styleProp: 'paddingLeft', transform: resolveSpacingTransform},
  {prop: 'paddingRight', styleProp: 'paddingRight', transform: resolveSpacingTransform},
];

export interface BoxProps extends TestIdProps {
  children?: ReactNode;
  center?: boolean;

  // Accessibility & DOM
  role?: AriaRole;
  id?: string;
  tabIndex?: number;

  // Padding
  padding?: Integer | ResponsiveValue<Integer>;
  paddingHorizontal?: Integer | ResponsiveValue<Integer>;
  paddingVertical?: Integer | ResponsiveValue<Integer>;
  paddingTop?: Integer | ResponsiveValue<Integer>;
  paddingBottom?: Integer | ResponsiveValue<Integer>;
  paddingLeft?: Integer | ResponsiveValue<Integer>;
  paddingRight?: Integer | ResponsiveValue<Integer>;

  // Sizing
  width?: Integer | CSSDimension | ResponsiveValue<Integer | CSSDimension>;
  height?: Integer | CSSDimension | ResponsiveValue<Integer | CSSDimension>;
  maxWidth?: Integer | CSSDimension | ResponsiveValue<Integer | CSSDimension>;
  maxHeight?: Integer | CSSDimension | ResponsiveValue<Integer | CSSDimension>;
  minWidth?: Integer | CSSDimension | ResponsiveValue<Integer | CSSDimension>;
  minHeight?: Integer | CSSDimension | ResponsiveValue<Integer | CSSDimension>;

  // Flexbox
  flex?: number | string;
  flexGrow?: number;
  flexShrink?: number;
  flexBasis?: string;

  // Visual
  backgroundColor?: ThemeColorPath;
  border?: string;
  borderTop?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRight?: string;
  borderColor?: ThemeColorPath;
  borderRadius?: ThemeRadiusPath | ResponsiveValue<ThemeRadiusPath>;
  borderTopLeftRadius?: ThemeRadiusPath | ResponsiveValue<ThemeRadiusPath | undefined>;
  borderTopRightRadius?: ThemeRadiusPath | ResponsiveValue<ThemeRadiusPath | undefined>;
  borderBottomLeftRadius?: ThemeRadiusPath | ResponsiveValue<ThemeRadiusPath | undefined>;
  borderBottomRightRadius?: ThemeRadiusPath | ResponsiveValue<ThemeRadiusPath | undefined>;
  boxShadow?: ThemeShadowsPath;
  opacity?: number;

  // Positioning
  position?: 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';
  top?: Integer | CSSDimension;
  bottom?: Integer | CSSDimension;
  left?: Integer | CSSDimension;
  right?: Integer | CSSDimension;
  zIndex?: ThemeZIndexPath;

  // Overflow
  overflow?: 'auto' | 'hidden' | 'scroll' | 'visible';
  overflowX?: 'auto' | 'hidden' | 'scroll' | 'visible';
  overflowY?: 'auto' | 'hidden' | 'scroll' | 'visible';

  // Other
  transition?: string;
  ratio?: CSSProperties['aspectRatio'];
  contain?: CSSProperties['contain'];
  pointerEvents?: CSSProperties['pointerEvents'];
}

/**
 * Specific padding mappings for individual sides (used for override priority)
 */
const SPECIFIC_PADDING_MAPPINGS: ReadonlyArray<StyleMapping> = PADDING_STYLE_MAPPINGS.slice(1);

/**
 * Maps ThemeRadiusPath to CSS variable value
 */
const RADIUS_CSS_VARS: Record<ThemeRadiusPath, string> = {
  xs: 'var(--radius-xs)',
  sm: 'var(--radius-sm)',
  md: 'var(--radius-md)',
  lg: 'var(--radius-lg)',
  xl: 'var(--radius-xl)',
  full: 'var(--radius-full)',
  xSmall: 'var(--radius-xs)',
  small: 'var(--radius-sm)',
  medium: 'var(--radius-md)',
  large: 'var(--radius-lg)',
  circular: 'var(--radius-full)',
};

/**
 * Resolves corner radius to CSS value (handles responsive values by taking base value)
 */
const resolveCornerRadius = (
  value: ThemeRadiusPath | ResponsiveValue<ThemeRadiusPath | undefined> | undefined
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (isResponsiveValue(value)) {
    const baseValue = value.base;
    return baseValue !== undefined ? RADIUS_CSS_VARS[baseValue] : undefined;
  }
  return RADIUS_CSS_VARS[value];
};

/**
 * Converts BoxProps to Record for schema builder
 */
const toRecord = (props: BoxProps): Record<string, unknown> =>
  Object.fromEntries(Object.entries(props));

/**
 * Builds padding-specific styles, handling horizontal/vertical shortcuts
 * These expand to multiple properties and can't use the standard schema
 */
const buildPaddingStyles = (props: BoxProps): CSSProperties => {
  const propsRecord = toRecord(props);
  const style: CSSProperties = {};

  // Base padding styles from schema
  const basePadding = buildStylesFromSchema(propsRecord, PADDING_STYLE_MAPPINGS);
  Object.assign(style, basePadding);

  // Handle horizontal shortcut (expands to left + right)
  if (props.paddingHorizontal !== undefined) {
    const paddingHorizontalValue = resolveSpacingValue(props.paddingHorizontal);
    if (paddingHorizontalValue !== undefined) {
      style.paddingLeft = paddingHorizontalValue;
      style.paddingRight = paddingHorizontalValue;
    }
  }

  // Handle vertical shortcut (expands to top + bottom)
  if (props.paddingVertical !== undefined) {
    const paddingVerticalValue = resolveSpacingValue(props.paddingVertical);
    if (paddingVerticalValue !== undefined) {
      style.paddingTop = paddingVerticalValue;
      style.paddingBottom = paddingVerticalValue;
    }
  }

  // Re-apply specific overrides (paddingTop, etc.) after shortcuts
  // This ensures specific values take precedence over shortcuts
  const specificPadding = buildStylesFromSchema(propsRecord, SPECIFIC_PADDING_MAPPINGS);
  Object.assign(style, specificPadding);

  return style;
};

/**
 * Box component - fundamental building block for layouts
 * Styled div with type-safe spacing, colors, and responsive support
 *
 * Spacing is always applied via inline styles for simplicity and flexibility.
 * This matches the old foundation behavior (--eag-space-step * n rem).
 *
 * @example
 * <Box padding={4} backgroundColor="accent">Content</Box>
 * <Box padding={{ base: 2, sm: 4, lg: 8 }}>Responsive padding</Box>
 */
export const Box = forwardRef<HTMLDivElement, BoxProps>((props, ref) => {
  // Static class lookups
  const backgroundColorClass = getStaticClass(props.backgroundColor, BG_COLOR_CLASSES);
  const borderRadiusClass = getResponsiveStaticClass(props.borderRadius, RADIUS_CLASSES);
  const boxShadowClass = getStaticClass(props.boxShadow, SHADOW_CLASSES);
  const positionClass = props.position
    ? getStaticClass(props.position, POSITION_CLASSES)
    : undefined;
  const overflowClass = getStaticClass(props.overflow, OVERFLOW_CLASSES);
  const overflowXClass = getStaticClass(props.overflowX, OVERFLOW_X_CLASSES);
  const overflowYClass = getStaticClass(props.overflowY, OVERFLOW_Y_CLASSES);

  // Build corner radius styles (inline for corner-specific control)
  const cornerRadiusStyles: CSSProperties = {
    borderTopLeftRadius: resolveCornerRadius(props.borderTopLeftRadius),
    borderTopRightRadius: resolveCornerRadius(props.borderTopRightRadius),
    borderBottomLeftRadius: resolveCornerRadius(props.borderBottomLeftRadius),
    borderBottomRightRadius: resolveCornerRadius(props.borderBottomRightRadius),
  };

  // Build inline styles using schema-based builder
  const style: CSSProperties = {
    ...buildPaddingStyles(props),
    ...buildStylesFromSchema(toRecord(props), [
      ...SIZING_STYLE_MAPPINGS,
      ...FLEX_ITEM_STYLE_MAPPINGS,
      ...BORDER_STYLE_MAPPINGS,
      ...POSITIONING_STYLE_MAPPINGS,
      ...MISC_STYLE_MAPPINGS,
    ]),
    ...cornerRadiusStyles,
    contain: props.contain,
    pointerEvents: props.pointerEvents,
  };

  // Filter out undefined values from style
  const filteredStyle = Object.fromEntries(
    Object.entries(style).filter(([, value]) => value !== undefined)
  ) as CSSProperties;

  const className = cn(
    props.center && 'mx-auto',
    backgroundColorClass,
    borderRadiusClass,
    boxShadowClass,
    positionClass,
    overflowClass,
    overflowXClass,
    overflowYClass
  );

  return (
    <div
      ref={ref}
      id={props.id}
      role={props.role}
      tabIndex={props.tabIndex}
      className={className}
      style={Object.keys(filteredStyle).length > 0 ? filteredStyle : undefined}
      data-testid={suffixTestId('box', props)}
    >
      {props.children}
    </div>
  );
});
